import React from "react";
import { toast } from "react-toastify";
import { CopyOutlined } from "@ant-design/icons";
import { STATUS, MONO } from "../../shellTokens";

/**
 * The courier handover, on the order detail's Delivery panel.
 *
 * Two independent facts are shown, and keeping them apart is the point. Our
 * handover — did we get the parcel to them — is `status`. Their report about it
 * afterwards is `courierStatus` / `reviewReason`. A perfectly booked consignment
 * is exactly the one that can come back cancelled, so a single merged "state"
 * would hide the case that matters most.
 *
 * Renders nothing for hand-booked orders, which is every order placed before the
 * integration and every one on a manual channel.
 */

/** Their vocabulary, in words the floor uses. */
const COURIER_STATUS_LABEL = {
  in_review: "Being reviewed by courier",
  pending: "In transit",
  delivered: "Delivered",
  delivered_approval_pending: "Delivered (payment settling)",
  partial_delivered: "Partially delivered",
  partial_delivered_approval_pending: "Partially delivered (payment settling)",
  cancelled: "Cancelled by courier",
  cancelled_approval_pending: "Cancelled (payment settling)",
  hold: "On hold at courier",
  unknown: "Unknown to courier",
  unknown_approval_pending: "Unknown (payment settling)",
};

/**
 * What a flagged consignment needs a person to do. Each of these was reported by
 * the courier and deliberately *not* applied to the order: an exit needs a
 * stated reason and a per-line Sellable/Damaged decision, which no webhook has.
 */
const REVIEW_LABEL = {
  COURIER_CANCELLED: "The courier cancelled this delivery.",
  COURIER_PARTIAL_DELIVERED: "The courier delivered this only in part.",
  COURIER_HOLD: "The courier is holding this parcel.",
  COURIER_UNKNOWN: "The courier cannot account for this parcel.",
  COURIER_UNRECOGNISED: "The courier sent a status we do not recognise.",
};

const rowStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  font: "400 12.5px Outfit, sans-serif",
  color: "var(--text)",
  marginTop: 4,
};

export default function CourierPanel({ consignment }) {
  if (!consignment) return null;

  const {
    status,
    trackingCode,
    consignmentId,
    courierStatus,
    reviewReason,
    lastError,
  } = consignment;

  const copy = () => {
    navigator.clipboard?.writeText(trackingCode).then(
      () => toast.success("Tracking code copied"),
      () => toast.error("Could not copy the tracking code"),
    );
  };

  return (
    <>
      <div style={{ height: 1, background: "var(--border)" }} />

      <div>
        <div
          style={{
            font: "600 11px Outfit, sans-serif",
            color: "var(--dim)",
            textTransform: "uppercase",
            letterSpacing: ".05em",
            marginBottom: 4,
          }}
        >
          Courier
        </div>

        {trackingCode ? (
          <div style={rowStyle}>
            {/* "Booking No." is what the floor calls it, and what the printed
                invoice is labelled, so the screen and the paper agree. */}
            <span>Booking No.</span>
            <button
              type="button"
              onClick={copy}
              title="Copy tracking code"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                border: 0,
                background: "none",
                padding: 0,
                cursor: "pointer",
                font: `600 12.5px ${MONO}`,
                color: "var(--primary)",
              }}
            >
              {trackingCode}
              <CopyOutlined style={{ fontSize: 11 }} />
            </button>
          </div>
        ) : (
          <HandoverState status={status} lastError={lastError} />
        )}

        {/* Their own record number. Secondary — it is what we quote back to
            SteadFast support, not what identifies the parcel to anyone else. */}
        {consignmentId && (
          <div style={rowStyle}>
            <span>Consignment</span>
            <span style={{ fontFamily: MONO, fontSize: 12, color: "var(--muted)" }}>
              {consignmentId}
            </span>
          </div>
        )}

        {courierStatus && (
          <div style={rowStyle}>
            <span>Parcel</span>
            <span style={{ fontWeight: 600 }}>
              {COURIER_STATUS_LABEL[courierStatus] ?? courierStatus}
            </span>
          </div>
        )}

        {/* Booked, but the courier has said something a person must answer. */}
        {reviewReason && (
          <Notice tint={STATUS.warn}>
            {REVIEW_LABEL[reviewReason] ?? reviewReason} The order has not been
            moved — cancel or return it yourself once you have decided what
            happened to the goods.
          </Notice>
        )}

        {/* A booked consignment carrying a note is the recovered-by-invoice
            case: real, but with no codes we could read back. */}
        {status === "BOOKED" && trackingCode && lastError && (
          <Notice tint={STATUS.warn}>{lastError}</Notice>
        )}
      </div>
    </>
  );
}

/** Where the handover got to, when there is no tracking code to show yet. */
function HandoverState({ status, lastError }) {
  if (status === "PENDING") {
    return (
      <>
        <div style={rowStyle}>
          <span>Booking</span>
          <span style={{ fontWeight: 600, color: STATUS.warn }}>
            Queued — will retry
          </span>
        </div>
        {lastError && <Notice tint={STATUS.warn}>{lastError}</Notice>}
      </>
    );
  }

  if (status === "FAILED") {
    return (
      <>
        <div style={rowStyle}>
          <span>Booking</span>
          <span style={{ fontWeight: 600, color: STATUS.error }}>
            Failed — needs attention
          </span>
        </div>
        {lastError && <Notice tint={STATUS.error}>{lastError}</Notice>}
      </>
    );
  }

  // BOOKED with no tracking code: recovered by invoice lookup, which their API
  // answers without ever returning the codes.
  return (
    <>
      <div style={rowStyle}>
        <span>Booking</span>
        <span style={{ fontWeight: 600 }}>Booked</span>
      </div>
      {lastError && <Notice tint={STATUS.warn}>{lastError}</Notice>}
    </>
  );
}

function Notice({ tint, children }) {
  return (
    <div
      style={{
        marginTop: 6,
        padding: "6px 8px",
        borderRadius: 8,
        borderLeft: `2px solid ${tint}`,
        background: "var(--surface2)",
        font: "400 11.5px Outfit, sans-serif",
        color: "var(--muted)",
        lineHeight: 1.45,
      }}
    >
      {children}
    </div>
  );
}
